import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Download, ArrowRight } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import {
  ACOUSTIC_CAMERAS,
  COMPARE_ROW_GROUPS,
  getCameraById,
  getCellValue,
} from '../data/acousticCameras';

/**
 * Modal that lets a visitor on any individual acoustic-camera product
 * page compare the model they're on against any other model in the line.
 *
 * Props:
 *   open        boolean — controls modal visibility
 *   onOpenChange (bool) => void — visibility setter
 *   primaryId   string — id of the camera the product page is for
 *                        (matches an entry in ACOUSTIC_CAMERAS)
 */
const CompareCamerasModal = ({ open, onOpenChange, primaryId, defaultOtherId }) => {
  const others = ACOUSTIC_CAMERAS.filter((c) => c.id !== primaryId);
  const [otherId, setOtherId] = useState(defaultOtherId || others[0]?.id || '');

  // If the primary camera or the requested defaultOtherId changes, sync
  // the picker so the sticky compare bar can jump into a specific pair.
  useEffect(() => {
    if (defaultOtherId && defaultOtherId !== primaryId) {
      setOtherId(defaultOtherId);
    } else {
      setOtherId(others[0]?.id || '');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [primaryId, defaultOtherId]);

  const primary = getCameraById(primaryId);
  const other = getCameraById(otherId);
  if (!primary) return null;

  const columns = other ? [primary, other] : [primary];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-3xl w-[95vw] max-h-[90vh] overflow-hidden flex flex-col p-0"
        data-testid="compare-cameras-modal"
      >
        <DialogHeader className="px-6 pt-6 pb-4 border-b border-slate-200">
          <DialogTitle className="text-2xl font-bold text-[#1e2d3b]">
            Compare Acoustic Cameras
          </DialogTitle>
          <p className="text-sm text-slate-500 mt-1">
            Pick another model to compare against{' '}
            <span className="font-semibold text-[#1e2d3b]">FOTRIC {primary.name}</span>.
          </p>
        </DialogHeader>

        {/* Model picker */}
        <div className="px-6 pt-4 pb-3 border-b border-slate-100 flex items-center gap-3 flex-wrap">
          <label
            htmlFor="compare-modal-other-select"
            className="text-sm font-semibold text-slate-700"
          >
            Compare with:
          </label>
          <select
            id="compare-modal-other-select"
            value={otherId}
            onChange={(e) => setOtherId(e.target.value)}
            data-testid="compare-modal-select"
            className="rounded-lg border border-slate-300 bg-white text-sm font-medium text-[#1e2d3b] px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            {others.map((c) => (
              <option key={c.id} value={c.id}>
                FOTRIC {c.name} — {c.tagline}
              </option>
            ))}
          </select>
        </div>

        {/* Scrollable spec grid */}
        <div className="flex-1 overflow-y-auto px-6 py-4">
          {/* Product header row */}
          <div className="grid grid-cols-2 gap-4 mb-4">
            {columns.map((cam, idx) => (
              <div
                key={cam.id}
                className={`p-4 rounded-xl border-2 ${
                  idx === 0
                    ? 'border-blue-500 bg-blue-50/30'
                    : 'border-slate-200 bg-white'
                }`}
                data-testid={`compare-modal-col-${cam.id}`}
              >
                <div className="h-24 flex items-center justify-center mb-3">
                  {cam.image ? (
                    <img
                      src={cam.image}
                      alt={`FOTRIC ${cam.name}`}
                      className="max-h-24 w-auto object-contain"
                      loading="lazy"
                    />
                  ) : null}
                </div>
                <p className="text-base font-bold text-[#1e2d3b] text-center leading-tight">
                  FOTRIC {cam.name}
                </p>
                <p className="text-xs text-blue-600 text-center font-medium mb-3">
                  {cam.tagline}
                </p>
                <div className="flex flex-col gap-2">
                  <Link
                    to={cam.productPage}
                    onClick={() => onOpenChange(false)}
                    className="inline-flex items-center justify-center gap-1 text-xs font-semibold text-blue-600 hover:text-orange-500 transition-colors"
                    data-testid={`compare-modal-learn-more-${cam.id}`}
                  >
                    Learn more
                    <ArrowRight className="w-3 h-3" />
                  </Link>
                  {cam.datasheet ? (
                    <a
                      href={cam.datasheet}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center justify-center gap-1.5 text-[11px] font-semibold px-3 py-1.5 rounded-full bg-slate-900 text-white hover:bg-slate-700 transition-colors"
                      data-testid={`compare-modal-datasheet-${cam.id}`}
                    >
                      <Download className="w-3 h-3" />
                      Data sheet
                    </a>
                  ) : (
                    <Link
                      to="/#contact"
                      onClick={() => onOpenChange(false)}
                      className="inline-flex items-center justify-center gap-1.5 text-[11px] font-semibold px-3 py-1.5 rounded-full border border-slate-300 text-slate-700 hover:border-blue-500 hover:text-blue-600 transition-colors"
                      data-testid={`compare-modal-request-datasheet-${cam.id}`}
                    >
                      <Download className="w-3 h-3" />
                      Request data sheet
                    </Link>
                  )}
                </div>
              </div>
            ))}
          </div>

          {/* Grouped spec rows */}
          {COMPARE_ROW_GROUPS.map((group) => (
            <div key={group.label} className="mb-5">
              <p className="text-[11px] uppercase tracking-widest font-bold text-slate-500 mb-2 border-b border-slate-100 pb-1">
                {group.label}
              </p>
              <dl className="space-y-2">
                {group.rows.map((row) => (
                  <div
                    key={row.key}
                    className="grid grid-cols-[minmax(0,1fr)_minmax(0,2fr)] gap-3 text-xs"
                  >
                    <dt className="font-medium text-slate-500 pt-0.5">{row.label}</dt>
                    <dd className="grid grid-cols-2 gap-3 text-slate-700">
                      {columns.map((cam) => {
                        const val = getCellValue(cam, row) || '—';
                        const isDash = val === '—';
                        return (
                          <span
                            key={cam.id}
                            data-testid={`compare-modal-cell-${cam.id}-${row.key}`}
                            className={`leading-snug ${isDash ? 'text-slate-300' : ''}`}
                          >
                            {val}
                          </span>
                        );
                      })}
                    </dd>
                  </div>
                ))}
              </dl>
            </div>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default CompareCamerasModal;
